import { useEffect, useState } from "react";
import { useLoop } from "../loop-provider";
import { Drawer, Text, Button, Form, useDrawer, useToast, Icons } from "../../design";
import { translate } from "../../utils/translations";
import { handleError, useAxios } from "../../utils/helpers";
import SelectStartDateDrawer from "./create-subscription-start-date";
import { format, addDays, subDays } from "date-fns";

const _ADD_DAYS = 2;

export default function CreateNewSubscriptionV2(props: any) {
    const drawers = useDrawer();
    const [axios] = useAxios();
    const toasts = useToast();

    const { myshopify_domain, customer_shopify_id, deliveryPreferences } = useLoop();

    const [isLoading, setIsLoading] = useState(false);
    const [availablePaymentMethods, setAvailablePaymentMethods] = useState([]);
    const [chargeOffset, setChargeOffset] = useState(0);
    const [selectedStartDate, setSelectedStartDate]: Array<any> = useState(
        addDays(addDays(new Date(), chargeOffset), _ADD_DAYS),
    );
    const [selectedPaymentMethodGraphQLId, setSelectedPaymentMethodGraphQLId]: Array<any> = useState("");

    const handleSelectedDateChange = (dateEpoch: number) => {
        setSelectedStartDate(new Date(dateEpoch * 1000));
    };

    const toggleDateSelect = () => {
        drawers.open({
            component: SelectStartDateDrawer,
            props: {
                currentDate: selectedStartDate.getTime() / 1000,
                handleDateSelect: handleSelectedDateChange,
                billing_policy: {
                    anchor_type: props.selectedSellingPlan.billing_anchor_type,
                    anchor_day: props.selectedSellingPlan.billing_anchor_day,
                    anchor_month: props.selectedSellingPlan.billing_anchor_month,
                },
            },
        });
    };

    const getDisableBeforeDates = () => {
        let _chargeOffset = undefined;
        for (const _pref of deliveryPreferences) {
            if (
                _pref.defaultPreferenceKey === "delivery_scheduled_order" &&
                _pref.storePreferenceEnabled &&
                _pref.inputValueInt
            ) {
                _chargeOffset = _pref.inputValueInt;
                break;
            } else {
                _chargeOffset = 0;
            }
        }
        setChargeOffset(_chargeOffset);
        setSelectedStartDate(addDays(addDays(new Date(), _chargeOffset), _ADD_DAYS));
    };

    const loadData = async () => {
        setIsLoading(true);
        try {
            const [{ data: pmData }] = await Promise.all([
                axios.get(`${process.env.NEXT_PUBLIC_LOOP_BACKEND}/api/customer/v2/available_payment_methods`),
            ]);
            setAvailablePaymentMethods(pmData.data);
            if (pmData.data.length > 0) {
                setSelectedPaymentMethodGraphQLId(pmData.data[0].admin_graphql_api_id);
            }
        } catch (error) {
            handleError(error, "components/drawers/create-subscription", "loadData", {
                myshopify_domain,
                customer_shopify_id,
            });
        }
        setIsLoading(false);
    };

    const handleConfirm = async () => {
        setIsLoading(true);
        try {
            const _startDate = subDays(selectedStartDate, chargeOffset);
            let body = {
                products: [
                    {
                        variant_shopify_id: props.selectedVariant.variant_shopify_id,
                        quantity: props.quantity,
                    },
                ],
                sellingPlan_id: props.selectedSellingPlan.id,
                start_date: _startDate.toISOString(),
                paymentMethod_graphql_id: selectedPaymentMethodGraphQLId,
                shipping_address: props.availableShippingAddress,
            };
            const { data }: any = await axios.post(
                `${process.env.NEXT_PUBLIC_LOOP_BACKEND}/api/customer/v2/create`,
                body,
            );
            toasts.open({
                message: data.message,
                type: data.success ? "success" : "failure",
                time: 10000,
            });
            if (data.success) {
                props.toggleActive();
                props.reloadContracts();
                props.closeAll();
            }
        } catch (error) {
            handleError(error, "components/drawers/create-new-subscription-v2", "handleConfirm", {
                myshopify_domain,
                customer_shopify_id,
            });
        }
        setIsLoading(false);
    };

    useEffect(() => {
        getDisableBeforeDates();
        loadData();
    }, []);

    return (
        <Drawer
            title={translate("create_new_subscription_v2_header")}
            active={props.active}
            toggleActive={props.toggleActive}
            id="create-new-subscription-v2"
        >
            <Drawer.Body>
                {isLoading ? (
                    <Icons.Spinner />
                ) : (
                    <>
                        <Text.P1>{translate("create_new_subscription_description")}</Text.P1>
                        <div className="my-2">
                            <Form.Input
                                className="mt-1"
                                label={translate("subscription_create_drawer_start_date_label")}
                                value={format(selectedStartDate, "LLL d, yyyy")}
                                onClick={toggleDateSelect}
                            />
                        </div>
                        <div className="my-2">
                            <Form.Select
                                className="mt-1"
                                label={translate("subscription_create_drawer_payment_method_label")}
                                value={selectedPaymentMethodGraphQLId}
                                onChange={(event: any) => {
                                    setSelectedPaymentMethodGraphQLId(event.target.value);
                                }}
                                options={availablePaymentMethods.map((pm: any) => {
                                    let _str = "";
                                    let _instrumentType = pm.instrument_type;
                                    let _instrument = pm.payment_instrument;

                                    if (
                                        _instrumentType == "CustomerCreditCard" ||
                                        _instrumentType == "CustomerRemoteCreditCard" ||
                                        _instrumentType == "AuthorizeNetCustomerPaymentProfile" ||
                                        _instrumentType == "CustomerRemoteBraintreeCreditCard"
                                    ) {
                                        let _brand = _instrument.brand;
                                        let _lastDigits = _instrument.virtualLastDigits
                                            ? _instrument.virtualLastDigits
                                            : _instrument.lastDigits
                                              ? _instrument.lastDigits
                                              : _instrument.last_digits
                                                ? _instrument.last_digits
                                                : "";
                                        let _expiryMonth = _instrument.expiryMonth
                                            ? _instrument.expiryMonth
                                            : _instrument.month
                                              ? _instrument.month
                                              : "";
                                        let _expiryYear = _instrument.expiryYear
                                            ? _instrument.expiryYear % 100
                                            : _instrument.year
                                              ? _instrument.year % 100
                                              : "";
                                        _str =
                                            translate("payment_card_details_text", {
                                                card_brand: _brand,
                                                last_digits: _lastDigits,
                                            }) +
                                            ", " +
                                            translate("payment_card_expiry_text", {
                                                expiryMonth: _expiryMonth,
                                                expiryYear: _expiryYear,
                                            });
                                    } else if (_instrumentType == "CustomerShopPayAgreement") {
                                        let _brand = "ShopPay";
                                        let _lastDigits = _instrument.virtualLastDigits
                                            ? _instrument.virtualLastDigits
                                            : _instrument.lastDigits
                                              ? _instrument.lastDigits
                                              : _instrument.last_digits
                                                ? _instrument.last_digits
                                                : "";
                                        let _expiryMonth = _instrument.expiryMonth
                                            ? _instrument.expiryMonth
                                            : _instrument.month
                                              ? _instrument.month
                                              : "";
                                        let _expiryYear = _instrument.expiryYear
                                            ? _instrument.expiryYear % 100
                                            : _instrument.year
                                              ? _instrument.year % 100
                                              : "";
                                        _str =
                                            translate("payment_card_details_text", {
                                                card_brand: _brand,
                                                last_digits: _lastDigits,
                                            }) +
                                            ", " +
                                            translate("payment_card_expiry_text", {
                                                expiryMonth: _expiryMonth,
                                                expiryYear: _expiryYear,
                                            });
                                    } else if (
                                        _instrumentType == "CustomerPaypalBillingAgreement" ||
                                        _instrumentType == "PaypalBillingAgreement"
                                    ) {
                                        if (_instrument.paypalAccountEmail) {
                                            _str = translate("paypal_account_with_email_text", {
                                                email: _instrument.paypalAccountEmail,
                                            });
                                        } else {
                                            _str = translate("paypal_account_without_email_text");
                                        }
                                    }
                                    return {
                                        label: _str,
                                        value: pm.admin_graphql_api_id,
                                    };
                                })}
                            />
                        </div>
                    </>
                )}
            </Drawer.Body>
            <Drawer.Footer>
                <div className="d-flex flex-column">
                    <Button
                        size="lg"
                        className="m-2"
                        onClick={handleConfirm}
                        disabled={!selectedPaymentMethodGraphQLId || !selectedStartDate || isLoading}
                    >
                        {translate("subscription_create_drawer_start_btn")}
                    </Button>
                    <Button
                        size="lg"
                        variant="underline"
                        className="m-2"
                        onClick={props.toggleActive}
                        disabled={isLoading}
                    >
                        {translate("go_back_button")}
                    </Button>
                </div>
            </Drawer.Footer>
        </Drawer>
    );
}
